import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { AudioProvider, useAudio } from '../audio-player/internal/AudioContext';
import Spinner, { SpinnerSizes, } from '@polestar/component-warehouse-react/components/spinner/Spinner';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import Icon from '@polestar/component-warehouse-react/components/icon/Icon';
import { motion, useMotionValue } from 'framer-motion';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { useAudioControls, } from '@polestar/component-warehouse-react/components/audio-player/useAudioControls';
import { focusProviderOptions } from '@polestar/component-warehouse-react/contexts/focus';
const SoundPlayer = forwardRef(({ audioControls, ...props }, ref) => {
    const _controls = useAudioControls();
    return (React.createElement(AudioProvider, { muted: props.muted, autoPlay: props.autoPlay, controls: audioControls || _controls },
        React.createElement(SoundPlayerWithoutContext, { ref: ref, ...props })));
});
SoundPlayer.displayName = 'SoundPlayer';
export default SoundPlayer;
const SoundPlayerWithoutContext = forwardRef(({ src, ariaPlayLabel = 'play sound', disabled = false, ...props }, ref) => {
    const { audioRef: playerRef, playing, setPlaying, canPlay } = useAudio();
    const combinedRef = useCombinedRef(ref, playerRef);
    const [analyser, setAnalyser] = useState(null);
    const [audioContext, setAudioContext] = useState();
    const [audioSource, setAudioSource] = useState();
    const scale = useMotionValue(1);
    const handlePlayEnded = useCallback(() => {
        setPlaying(false);
    }, [setPlaying]);
    const handlePlay = useCallback(() => {
        if (playerRef.current) {
            // @ts-ignore
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (playing) {
                // stop
                playerRef.current.pause();
                playerRef.current.currentTime = 0;
                setPlaying(false);
            }
            else {
                playerRef.current.play();
                if (AudioContext && audioContext === undefined && audioSource === undefined) {
                    const context = new AudioContext();
                    const analyserNode = context.createAnalyser();
                    analyserNode.fftSize = 32;
                    playerRef.current.onplay = () => context.resume();
                    const source = context.createMediaElementSource(playerRef.current);
                    source.connect(analyserNode).connect(context.destination);
                    setAudioContext(context);
                    setAudioSource(source);
                    setAnalyser(analyserNode);
                }
                setPlaying(true);
            }
        }
    }, [setPlaying, playerRef, playing, audioSource, audioContext]);
    useEffect(() => {
        const ref = playerRef.current;
        ref === null || ref === void 0 ? void 0 : ref.addEventListener('ended', handlePlayEnded);
        return () => {
            ref === null || ref === void 0 ? void 0 : ref.removeEventListener('ended', handlePlayEnded);
        };
    }, [playerRef, handlePlayEnded]);
    useEffect(() => {
        let raf;
        const updateScales = () => {
            if (analyser) {
                const bufferLength = analyser.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);
                // eslint-disable-next-line testing-library/prefer-explicit-assert
                analyser.getByteFrequencyData(dataArray);
                // 100% output = scale 1 // 0% output = scale 0.8125
                scale.set(0.8125 + (dataArray[4] / 256) * 0.1875);
                raf = requestAnimationFrame(updateScales);
            }
        };
        raf = requestAnimationFrame(updateScales);
        return () => {
            cancelAnimationFrame(raf);
        };
    }, [scale, analyser, playing]);
    return (React.createElement(Container, null,
        React.createElement("audio", { ref: combinedRef, src: src, ...props, autoPlay: false, muted: false, loop: false, controls: false }),
        !canPlay ? (React.createElement(SpinnerWrapper, null,
            React.createElement(Spinner, { size: SpinnerSizes.medium }))) : (React.createElement(React.Fragment, null,
            React.createElement(ControlIcon, { icon: playing ? Icons.stopFilled : Icons.volumeOnFilled, topOffset: false, playing: playing, disabled: disabled }),
            React.createElement(ControlButton, { type: "button", "data-type": "playbutton", onClick: handlePlay, playing: playing, "aria-label": ariaPlayLabel, title: ariaPlayLabel, style: { scale: playing ? scale : 1 }, disabled: disabled, ...focusProviderOptions({ round: true, spacing: 6 }) })))));
});
SoundPlayerWithoutContext.displayName = 'SoundPlayerWithoutContext';
const ControlButton = styled(motion.button)(({ theme, playing, disabled }) => css(css `
        cursor: pointer;
        transition: ${createCSSTransition('all', MotionEasings.easeOut, MotionDurations.fast)};
        background-color: ${theme.soundPlayer.states[playing ? 'active' : 'enabled']
    .backgroundColor};
        border: none;
        margin: ${Spacings.xSmall};
        border-radius: 50%;
        width: ${Spacings.xxLarge};
        height: ${Spacings.xxLarge};
        outline: none;
        display: flex;
        justify-content: center;
        align-items: center;
        touch-action: manipulation;
        user-select: none;
        -webkit-tap-highlight-color: transparent;

        &::-moz-focus-inner {
          border: none;
        }

        &:focus {
          outline: 0;
        }

        box-shadow:
          0 0 0 ${createCustomSpacing(6 / 16)} ${theme.polestarApp.focus.inner.color},
          0 0 0 ${Spacings.xSmall}
            ${theme.soundPlayer.states[playing ? 'active' : 'enabled'].borderColor};
      `, !playing &&
    css `
          @media (hover: hover) {
            &:hover {
              box-shadow:
                0 0 0 6px ${theme.polestarApp.focus.inner.color},
                0 0 0 8px ${theme.soundPlayer.states.hover.borderColor};
            }
          }
        `, playing &&
    css `
          @media (hover: hover) {
            &:hover {
              box-shadow:
                0 0 0 6px ${theme.polestarApp.focus.inner.color},
                0 0 0 8px ${theme.soundPlayer.states.enabled.borderColor};
            }
          }
        `, disabled &&
    css `
          pointer-events: none;
          background-color: ${theme.soundPlayer.states.disabled.backgroundColor};
          box-shadow:
            0 0 0 6px ${theme.polestarApp.focus.inner.color},
            0 0 0 8px ${theme.soundPlayer.states.disabled.borderColor};
        `));
const ControlIcon = styled(Icon)(({ theme, playing, disabled }) => css(css `
        pointer-events: none;
        user-select: none;
        inset-inline-start: 50%;
        inset-block-start: 50%;
        transform: translate(-50%, -50%);
        position: absolute;
        color: ${theme.soundPlayer.states[playing ? 'active' : 'enabled'].iconColor};
        z-index: 150;
      `, disabled &&
    css `
          color: ${theme.soundPlayer.states.disabled.iconColor};
        `));
const Container = styled.div `
  position: relative;
  width: ${Spacings.xxxLarge};
  height: ${Spacings.xxxLarge};
`;
const SpinnerWrapper = styled.div `
  position: absolute;
  inset-block-start: ${Spacings.large};
  inset-inline-start: ${Spacings.large};
  inset-inline-end: ${Spacings.large};
  inset-block-end: ${Spacings.large};
  display: flex;
  justify-content: center;
  align-items: center;
`;
